import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

const LogMileage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    miles: '',
    purpose: '',
    from_location: '',
    to_location: '',
    date: new Date().toISOString().split('T')[0]
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post('/solo/mileage', formData);
      
      setSuccess(
        `Mileage logged successfully! ${response.data.miles} miles at £${response.data.rate}/mile = £${response.data.claim} claim`
      );
      
      // Reset form
      setFormData({
        miles: '',
        purpose: '',
        from_location: '',
        to_location: '',
        date: new Date().toISOString().split('T')[0]
      });

      // Navigate back after 3 seconds
      setTimeout(() => {
        navigate('/solo-dashboard');
      }, 3000);

    } catch (err) {
      console.error('Error logging mileage:', err);
      setError(err.response?.data?.error || 'Failed to log mileage. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/solo-dashboard')}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <DirectionsCarIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Log Business Mileage
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Track business travel expenses (45p per mile for first 10,000 miles, 25p thereafter)
        </Typography>

        {/* Critical Warning About Double Counting */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            ⚠️ IMPORTANT: DO NOT DOUBLE COUNT
          </Typography>
          <Typography variant="caption">
            HMRC's simplified mileage allowance (45p/mile) covers <strong>ALL vehicle running costs</strong> including:
            <br />
            • Fuel • Insurance • Repairs • Servicing • MOT • Road Tax • Depreciation • Finance Payments
            <br /><br />
            <strong>DO NOT also claim these as separate expenses</strong> or you'll be double-counting and HMRC will reject your return.
            <br /><br />
            You must choose ONE method:
            <br />
            ✓ Use this Mileage Log (simplified - recommended for most)
            <br />
            OR
            <br />
            ✓ Track every fuel receipt, repair bill, insurance payment (actual costs - more paperwork)
            <br /><br />
            <strong>You cannot use both methods!</strong>
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Miles Travelled"
                name="miles"
                type="number"
                value={formData.miles}
                onChange={handleChange}
                required
                inputProps={{ step: '0.1', min: '0.1' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">miles</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="From"
                name="from_location"
                value={formData.from_location}
                onChange={handleChange}
                placeholder="e.g., Home, Salon"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="To"
                name="to_location"
                value={formData.to_location}
                onChange={handleChange}
                placeholder="e.g., Supplier, Client location"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                required
                placeholder="e.g., Meeting with supplier, Client home visit, Trade show"
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/solo-dashboard')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Logging...' : 'Log Mileage'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: 1, borderColor: 'info.light' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            HMRC Approved Mileage Rates (2024/25)
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            • First 10,000 miles: <strong>45p per mile</strong>
            <br />
            • Over 10,000 miles: <strong>25p per mile</strong>
            <br />
            <br />
            <strong>Important:</strong> Only business journeys qualify. Commuting from home to your 
            normal place of work does not count. Keep records of all business journeys for HMRC.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default LogMileage;